import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PaginatedTable from "../../components/Organismos/Table/PaginatedTable";
import { IPagination2, ISorting } from "../../interfaces/Components/ITable";
import {
  getEmailSendListPage,
  getEmailSendRecipients,
  IEmailSendListItem,
  IEmailSendRecipientRow,
} from "../../Services/EmailSend";
import { getEmailTemplateById } from "../../Services/EmailTemplate";
import { formatTimestampDate } from "../../utils/dates";
import { toastError } from "../../utils/toast";
import NewButton from "../../components/Atomos/Buttons/newButton";
import CloseModal from "../../components/Atomos/Icons/EditMode/CloseModal";
import Modal from "../../components/Atomos/Modals/Modal";

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_PAGINATION: IPagination2 = { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE };
const DEFAULT_SORTING: ISorting = { field: "sentDateTime", order: "desc" };
const TABLE_KEY = "email-template-logs";
const MODAL_TABLE_KEY = "email-log-recipients-modal";

export default function EmailTemplateLogsPage(): React.ReactElement {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [templateName, setTemplateName] = useState<string>("");
  const [rows, setRows] = useState<IEmailSendListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState<ISorting>(DEFAULT_SORTING);
  const [pagination, setPagination] = useState<IPagination2>(DEFAULT_PAGINATION);
  const [recipientModal, setRecipientModal] = useState<{
    emailSendId: string;
    listName: string;
  } | null>(null);
  const [recipientRows, setRecipientRows] = useState<IEmailSendRecipientRow[]>([]);
  const [recipientTotalCount, setRecipientTotalCount] = useState(0);
  const [recipientListName, setRecipientListName] = useState("");
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [recipientPagination, setRecipientPagination] = useState<IPagination2>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [recipientSorting, setRecipientSorting] = useState<ISorting>({
    field: "email",
    order: "asc",
  });

  useEffect(() => {
    if (!templateId) {
      return;
    }
    getEmailTemplateById(templateId)
      .then((template) => {
        if (template) {
          const name =
            [template.category, template.title].filter(Boolean).join(" - ") ||
            template.title ||
            "Email Template";
          setTemplateName(name);
        } else {
          setTemplateName("Email Template");
        }
      })
      .catch(() => setTemplateName("Email Template"));
  }, [templateId]);

  const fetchLogsPage = useCallback(async () => {
    if (!templateId) {
      return;
    }
    setIsLoading(true);
    try {
      const response = await getEmailSendListPage("sent", templateId, {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortField: sorting.field,
        sortOrder: sorting.order === "asc" ? "asc" : "desc",
      });
      setRows(response.items);
      setTotalCount(response.pagination.totalCount);
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message: unknown }).message)
          : "Unable to load email logs.";
      toastError(message);
      setRows([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [templateId, pagination.pageIndex, pagination.pageSize, sorting.field, sorting.order]);

  useEffect(() => {
    fetchLogsPage();
  }, [fetchLogsPage]);

  const openRecipientModal = useCallback((row: IEmailSendListItem) => {
    setRecipientModal({ emailSendId: row.id, listName: row.listName ?? "Recipient List" });
    setRecipientRows([]);
    setRecipientTotalCount(0);
    setRecipientListName(row.listName ?? "Recipient List");
    setRecipientPagination({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });
    setRecipientSorting({ field: "email", order: "asc" });
  }, []);

  useEffect(() => {
    if (!recipientModal?.emailSendId) {
      return;
    }
    setRecipientLoading(true);
    getEmailSendRecipients(recipientModal.emailSendId, {
      pageIndex: recipientPagination.pageIndex,
      pageSize: recipientPagination.pageSize,
      sortField: recipientSorting.field,
      sortOrder: recipientSorting.order === "asc" ? "asc" : "desc",
    })
      .then((data) => {
        setRecipientListName(data.listName?.trim() || recipientModal.listName);
        setRecipientRows(Array.isArray(data.recipients) ? data.recipients : []);
        setRecipientTotalCount(
          data.pagination?.totalCount ?? data.recipients?.length ?? 0
        );
      })
      .catch(() => {
        setRecipientRows([]);
        setRecipientTotalCount(0);
      })
      .finally(() => setRecipientLoading(false));
  }, [
    recipientModal?.emailSendId,
    recipientModal?.listName,
    recipientPagination.pageIndex,
    recipientPagination.pageSize,
    recipientSorting.field,
    recipientSorting.order,
  ]);

  const closeRecipientModal = useCallback(() => {
    setRecipientModal(null);
    setRecipientRows([]);
    setRecipientTotalCount(0);
  }, []);

  const paginatedRows = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    return rows.map((row, i) => ({
      ...row,
      index: start + i + 1,
      scheduledBy: row.scheduledBy ?? "—",
      listName: row.listName?.trim() ? row.listName : "—",
    }));
  }, [rows, pagination.pageIndex, pagination.pageSize]);

  const columnsDef = useMemo(
    () => [
      { id: "index", header: "#", width: 68, enableSorting: false },
      {
        id: "sentDateTime",
        header: "Sent Date",
        width: 180,
        cell: ({ row }: { row: IEmailSendListItem }) =>
          formatTimestampDate(row.sentDateTime),
      },
      { id: "scheduledBy", header: "Send/Scheduled By", width: 240 },
      { id: "listName", header: "Recipient List", width: 320 },
      {
        id: "recipientCount",
        header: "# of Recipients",
        width: 190,
        cell: ({ row }: { row: IEmailSendListItem }) => (
          <button
            type="button"
            onClick={() => openRecipientModal(row)}
            className="text-accent-Default hover:underline font-medium"
          >
            {row.recipientCount ?? 0}
          </button>
        ),
      },
    ],
    [openRecipientModal]
  );

  const paginatedRecipients = useMemo(() => {
    const start = recipientPagination.pageIndex * recipientPagination.pageSize;
    return recipientRows.map((r, i) => ({
      ...r,
      index: start + i + 1,
    }));
  }, [recipientRows, recipientPagination.pageIndex, recipientPagination.pageSize]);

  const recipientColumnsDef = useMemo(
    () => [
      { id: "index", header: "#", width: 68, enableSorting: false },
      { id: "contact", header: "Contact", width: 200 },
      { id: "email", header: "Email", width: 240 },
      { id: "firm", header: "Firm", width: 200 },
      { id: "location", header: "Location", width: 180 },
      { id: "region", header: "Region", width: 120 },
    ],
    []
  );

  if (!templateId) {
    navigate("/email-design", { replace: true });
    return <></>;
  }

  return (
    <main className="text-left">
      <div className="bg-white border border-gray-200 p-6 lg:p-8">
        <div className="flex flex-col gap-6">
          <div>
            <h1 className="text-3xl font-medium text-Default font-decimal">Email Logs</h1>
            <h2 className="mt-2 text-xl font-medium text-Default font-decimal">
              {templateName || "—"}
            </h2>
          </div>

          <PaginatedTable
            key={TABLE_KEY}
            data={paginatedRows}
            totalCount={totalCount}
            columnsDef={columnsDef}
            sorting={sorting}
            pagination={pagination}
            endpoint="emailSend"
            onSortingChange={setSorting}
            onPaginationChange={setPagination}
            showColumnVisibility={false}
            showExcelExport={false}
            emptyStateMessage={
              isLoading ? "Loading email logs..." : "No email dispatches found for this template."
            }
            tableKey={TABLE_KEY}
          />
        </div>
      </div>

      <Modal
        className="!text-Default py-6 px-6 md:py-8 md:px-8 lg:py-10 lg:px-10 max-w-4xl"
        isModalOpen={!!recipientModal}
        toggleModal={closeRecipientModal}
        title={(recipientListName?.trim() || recipientModal?.listName?.trim()) || "—"}
        titleColor="text-Default"
      >
        <div className="flex flex-col gap-4">
          {recipientLoading ? (
            <p className="text-gray-600">Loading recipients...</p>
          ) : (
            <PaginatedTable
              key={MODAL_TABLE_KEY}
              data={paginatedRecipients}
              totalCount={recipientTotalCount}
              columnsDef={recipientColumnsDef}
              sorting={recipientSorting}
              pagination={recipientPagination}
              onSortingChange={setRecipientSorting}
              onPaginationChange={setRecipientPagination}
              showColumnVisibility={false}
              showExcelExport={false}
              emptyStateMessage="No recipients."
              tableKey={MODAL_TABLE_KEY}
            />
          )}
          <div className="flex justify-end pt-2">
            <NewButton
              type="button"
              onClick={closeRecipientModal}
              content="textIcon"
              size="small"
              style="filled"
              color="accent"
              icon={<CloseModal /> as JSX.Element}
              text="Close"
              className="w-full sm:w-auto"
            />
          </div>
        </div>
      </Modal>
    </main>
  );
}
